import React from 'react'
import get from 'lodash/get'
import find from 'lodash/find'
import concat from 'lodash/concat'
import isNil from 'lodash/isNil'
import findIndex from 'lodash/findIndex'
import isNumber from 'lodash/isNumber'
import isFunction from 'lodash/isFunction'
import { List } from 'immutable'
import { connect as connectRedux } from 'react-redux'
import { getAuthIsLoggedIn } from '../../../../services/auth/index'
import {
  getCampaign, getVariation, getSubject, variationShouldRender,
} from '../../../../services/testarossa/index'
import TestarossaDefault from '../TestarossaDefault'
import TestarossaCase from '../TestarossaCase'

function variationIsMatch (variation, ids, notVariation) {
  const friendlyId = get(variation, 'friendlyId')
  if (!isNil(ids)) {
    const variationIds = concat([], ids)
    const match = find(variationIds, (id) => {
      return id === friendlyId
    })
    return isNumber(match)
  } if (!isNil(notVariation)) {
    const notVariationIds = concat([], notVariation)
    const match = find(notVariationIds, (id) => {
      return id === friendlyId
    })
    return !isNumber(match)
  }
  return true
}

function getChildren (props) {
  return concat([], props.children || [])
}

function getDefault (children) {
  return findIndex(children, (c) => (
    c && c.type === TestarossaDefault
  ))
}

function pickChild (children) {
  const index = findIndex(children, (child) => {
    const { type, props } = child
    const {
      campaign: id, variation: vids, notVariation, condition,
    } = props
    if (type === TestarossaCase) {
      const campaign = getCampaign({ id })
      const subject = getSubject({ campaign })
      const variation = getVariation({ campaign })

      if (variationIsMatch(variation, vids, notVariation)
        && variationShouldRender({ campaign, variation, subject })) {
        const conditionWasMet = isFunction(condition)
          ? !!condition(campaign, variation, subject) : true
        return conditionWasMet
      }
    } else if (type === TestarossaSwitch) {
      return pickChild(getChildren(props))
    }
    return false
  })
  if (index < 0) {
    return getDefault(children)
  }
  return index
}

function getInitialState (props) {
  const { bootstrapComplete, initialized, records } = props
  const children = getChildren(props)
  // SSR Testarossa removed: first paint must match HTML (default / null path)
  // until bootstrapComplete. Intentional campaign flicker after hydrate only.
  if (bootstrapComplete && initialized) {
    return {
      index: List.isList(records) ? pickChild(children) : getDefault(children),
      ready: true,
    }
  }
  const index = getDefault(children)
  return { index, ready: false }
}

class TestarossaSwitch extends React.Component {
  constructor (props) {
    super(props)
    this.state = getInitialState(props)
  }

  syncSelectedChild (index, ready = true) {
    const { index: currentIndex, ready: currentReady } = this.state
    if (currentIndex === index && currentReady === ready) {
      return
    }
    this.setState({ index, ready })
  }

  componentDidMount () {
    const { state, props } = this
    const { initialized, isLoggedIn, bootstrapComplete } = props
    const { ready } = state
    // Only pick campaigns after hydrate finishes (same contract as DaytonaSwitch).
    if (!bootstrapComplete) {
      return
    }
    if (isLoggedIn && ready !== true && initialized) {
      const children = getChildren(props)
      const index = pickChild(children)
      // eslint-disable-next-line react/no-did-mount-set-state
      this.syncSelectedChild(index, true)
    }

    if (!isLoggedIn) {
      const index = getDefault(getChildren(props))
      // eslint-disable-next-line react/no-did-mount-set-state
      this.syncSelectedChild(index, true)
    }
  }

  componentDidUpdate (prevProps) {
    const { records: prevRecords, context: prevContext } = prevProps
    const {
      records,
      context,
      failure,
      initialized,
      isLoggedIn,
      bootstrapComplete,
    } = this.props

    if (!isLoggedIn) {
      return
    }

    if (!bootstrapComplete || !initialized) {
      return
    }

    const children = getChildren(this.props)
    let nextIndex

    if (!prevProps.bootstrapComplete && bootstrapComplete) {
      nextIndex = List.isList(records) ? pickChild(children) : getDefault(children)
    } else if (failure) {
      nextIndex = getDefault(children)
    } else if (records !== prevRecords || context !== prevContext) {
      nextIndex = pickChild(children)
    } else {
      return
    }

    this.syncSelectedChild(nextIndex, true)
  }

  render () {
    const { state, props } = this
    const { index } = state
    const {
      isLoggedIn, initialized, records, bootstrapComplete,
    } = props
    const children = getChildren(props)

    // Logged-in: hide until Testarossa init after bootstrap (SSR HTML is empty).
    if (isLoggedIn && (!bootstrapComplete || !initialized)) {
      return null
    }

    if (isLoggedIn && !List.isList(records)) {
      return null
    }

    return children[index] || null
  }
}

const connectedTestarossaSwitch = connectRedux((state) => {
  const { testarossa, app, auth } = state
  const records = testarossa.get('records')
  const context = testarossa.get('context')
  const failure = testarossa.get('failure')
  const bootstrapComplete = app.get('bootstrapComplete')
  const isLoggedIn = getAuthIsLoggedIn(auth)
  const initialized = testarossa.get('initialized')

  return {
    records, context, failure, bootstrapComplete, isLoggedIn, initialized,
  }
})(TestarossaSwitch)

export default connectedTestarossaSwitch
